/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IMediaGalleryThumbnailItemViewProps,
    IMediaGalleryThumbnailsViewProps,
    IMediaGalleryViewProps,
    MediaType
} from '@msdyn365-commerce-modules/media-gallery';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IImageSettings } from '@msdyn365-commerce/core';
import React from 'react';

/**
 * Render the thumbnail item images.
 * @param thumbnail - The carousel thumbnail line props.
 * @returns Return HTML having thumbnail container props with image.
 */
const renderThumbnailItem = (thumbnail: IMediaGalleryThumbnailItemViewProps, mediaTypeValue?: MediaType): JSX.Element => {
    const { ThumbnailItemContainerProps, Picture } = thumbnail;
    const overlayClassName = mediaTypeValue === MediaType.Video ? 'ms-fullscreen-section__video-overlay' : 'ms-fullscreen-section__overlay';
    const iconClassName =
        mediaTypeValue === MediaType.Video ? 'ms-fullscreen-section__video-play-icon' : 'ms-fullscreen-section__magnifying-glass-icon';

    return (
        <Node {...ThumbnailItemContainerProps}>
            <Node className='ms-fullscreen-section'>
                {Picture}
                <Node className={overlayClassName}>
                    <span aria-hidden='true' className={iconClassName} />
                </Node>
            </Node>
        </Node>
    );
};

const getThumbnailImageSettings = (props: IMediaGalleryViewProps): IImageSettings => {
    const thumbnailImageSettings = props.config.thumbnailImageSettings;

    if (!thumbnailImageSettings) {
        return {
            ...defaultThumbnailImageSettings,
            disableLazyLoad: true
        };
    }

    if (thumbnailImageSettings.viewports) {
        return {
            ...thumbnailImageSettings,
            disableLazyLoad: true
        };
    }

    return {
        ...thumbnailImageSettings,
        viewports: defaultThumbnailImageSettings.viewports,
        lazyload: defaultThumbnailImageSettings.lazyload,
        cropFocalRegion: defaultThumbnailImageSettings.cropFocalRegion,
        disableLazyLoad: true
    };
};

const withThumbnailImageSettings = (
    picture: IMediaGalleryThumbnailItemViewProps['Picture'],
    props: IMediaGalleryViewProps
): IMediaGalleryThumbnailItemViewProps['Picture'] => {
    if (!React.isValidElement(picture)) {
        return picture;
    }

    return React.cloneElement(picture as React.ReactElement<{ imageSettings?: IImageSettings }>, {
        imageSettings: getThumbnailImageSettings(props)
    });
};

// Default image settings configured directly in code
const defaultThumbnailImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=64&h=64&q=80&m=6&f=webp', w: 64, h: 64 },
        sm: { q: 'w=80&h=80&q=80&m=6&f=webp', w: 80, h: 80 },
        md: { q: 'w=100&h=100&q=80&m=6&f=webp', w: 100, h: 100 },
        lg: { q: 'w=150&h=150&q=80&m=6&f=webp', w: 150, h: 150 },
        xl: { q: 'w=200&h=200&q=80&m=6&f=webp', w: 200, h: 200 }
    },
    lazyload: true,
    cropFocalRegion: true
};

// Default gallery image settings for main carousel images (fallback)
const defaultGalleryImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=546&h=546&q=80&m=6&f=webp', w: 546, h: 546 },
        sm: { q: 'w=738&h=738&q=80&m=6&f=webp', w: 738, h: 738 },
        md: { q: 'w=415&h=415&q=80&m=6&f=webp', w: 415, h: 415 },
        lg: { q: 'w=639&h=639&q=80&m=6&f=webp', w: 639, h: 639 },
        xl: { q: 'w=783&h=783&q=80&m=6&f=webp', w: 783, h: 783 }
    },
    lazyload: true,
    cropFocalRegion: true
};

/**
 * Render the Media gallery thumbnails to represent images in grid view.
 * @param thumbnails - The thumbnail view props.
 * @param props - The media gallery view props.
 * @returns - The single slide carousel component to render as media gallery image.
 */
const renderThumbnails = (thumbnails: IMediaGalleryThumbnailsViewProps, props: IMediaGalleryViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention --  Dependency from media-gallery.tsx file
    const { ThumbnailsContainerProps, SingleSlideCarouselComponentProps, items } = thumbnails;
    const mediaGalleryItems = props.state.mediaGalleryItems;

    return (
        <Node {...ThumbnailsContainerProps}>
            <Node {...SingleSlideCarouselComponentProps}>
                {items?.map((thumbnail, index) =>
                    renderThumbnailItem(
                        {
                            ...thumbnail,
                            Picture: withThumbnailImageSettings(thumbnail.Picture, props)
                        },
                        mediaGalleryItems?.[index]?.mediaTypeValue
                    )
                )}
            </Node>
        </Node>
    );
};

/**
 * Render the Media gallery items using viewprops.
 * @param props - The media gallery view props.
 * @returns The media gallery module wrapping up images node.
 */
const mediaGalleryView: React.FC<IMediaGalleryViewProps> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention --  Dependency from media-gallery.tsx file
    const { CarouselProps, Thumbnails, MediaGallery, Modal } = props;

    // Apply default gallery image settings if not configured or missing viewports
    // This ensures our viewport settings are used even if basic config exists
    if (!props.config.galleryImageSettings) {
        // Override the config to include our default gallery settings
        Object.assign(props.config, {
            galleryImageSettings: defaultGalleryImageSettings
        });
    } else if (!props.config.galleryImageSettings.viewports) {
        // Gallery settings exist but missing viewports - merge our viewport settings
        Object.assign(props.config.galleryImageSettings, {
            viewports: defaultGalleryImageSettings.viewports,
            lazyload: defaultGalleryImageSettings.lazyload,
            cropFocalRegion: defaultGalleryImageSettings.cropFocalRegion
        });
    }

    if (!props.config.thumbnailImageSettings) {
        Object.assign(props.config, {
            thumbnailImageSettings: defaultThumbnailImageSettings
        });
    } else if (!props.config.thumbnailImageSettings.viewports) {
        Object.assign(props.config.thumbnailImageSettings, {
            viewports: defaultThumbnailImageSettings.viewports,
            lazyload: defaultThumbnailImageSettings.lazyload,
            cropFocalRegion: defaultThumbnailImageSettings.cropFocalRegion
        });
    }

    return (
        <Module {...MediaGallery}>
            <Node {...CarouselProps} />
            {Modal}
            {renderThumbnails(Thumbnails, props)}
        </Module>
    );
};

export default mediaGalleryView;
